import { currentLocale, t as localeText, translateDisplayText } from "@/i18n/translate";
import type {
  MediaToolOperation,
  MediaToolReference,
  MediaToolSetting,
  MediaToolVariant,
} from "@/features/media-tools";
import type { V2Asset } from "../types";

export const audioToolGroups = [
  {
    value: "voice",
    get label() { return localeText("interface.voice_3091c844", { defaultValue: "Voice" }); },
    get description() { return localeText("interface.give_your_words_a_voice_91f8af86", { defaultValue: "Give your words a voice." }); },
    operations: [
      "text_to_speech",
      "dialogue",
      "voice_design",
      "voice_clone",
      "voice_clone_professional",
      "voice_manage",
      "voice_revoke",
      "voice_change",
      "transcribe",
      "forced_align",
      "pronunciation",
    ],
  },
  {
    value: "music",
    get label() { return localeText("interface.music_131260cb", { defaultValue: "Music" }); },
    get description() { return localeText("interface.write_a_soundtrack_shape_a_song_a0f7afb9", { defaultValue: "Write a soundtrack, shape a song." }); },
    operations: [
      "music_generate",
      "music_lyrics",
      "music_extend",
      "music_replace",
      "music_cover",
      "music_stems",
      "music_variants",
      "music_add_vocals",
      "music_add_instrumental",
      "music_midi",
    ],
  },
  {
    value: "sound_effects",
    get label() { return localeText("interface.sound_effects_a41e30af", { defaultValue: "Sound effects" }); },
    get description() { return localeText("interface.set_the_scene_with_sound_956755d1", { defaultValue: "Set the scene with sound." }); },
    operations: ["audio_generate", "sound_generate", "sound_reference", "sound_video_sync"],
  },
  {
    value: "audio_editing",
    get label() { return localeText("interface.audio_editing_8674dc6e", { defaultValue: "Audio editing" }); },
    get description() { return localeText("interface.cut_clean_and_finish_your_audio_5598d3ef", { defaultValue: "Cut, clean and finish your audio." }); },
    operations: [
      "import",
      "record",
      "trim",
      "split",
      "join",
      "fade",
      "volume",
      "mix",
      "normalize",
      "speed",
      "voice_isolate",
      "noise_cleanup",
      "source_separate",
      "dubbing",
      "subtitle_export",
    ],
  },
] as const;

export type AudioToolGroup = (typeof audioToolGroups)[number]["value"];
export interface AudioToolSelection {
  group: AudioToolGroup;
  operation: string;
  variantID?: string;
}
export const defaultAudioToolSelection: AudioToolSelection = {
  group: "voice",
  operation: "text_to_speech",
};

export function validateAudioToolSelection(
  raw: Record<string, unknown>,
): AudioToolSelection {
  if (raw.audio_tool === "studio") return defaultAudioToolSelection;
  const group =
    audioToolGroups.find((item) => item.value === raw.audio_group) ??
    audioToolGroups[0];
  const operation =
    typeof raw.audio_tool === "string" &&
    (group.operations as readonly string[]).includes(raw.audio_tool)
      ? raw.audio_tool
      : group.value === "voice"
        ? "text_to_speech"
        : group.operations[0];
  return {
    group: group.value,
    operation,
    ...(typeof raw.audio_variant === "string" &&
    /^variant-[a-f0-9]{64}$/.test(raw.audio_variant)
      ? { variantID: raw.audio_variant }
      : {}),
  };
}

export const audioToolDescriptions: Record<string, string> = {
  get audio_generate() { return translateDisplayText("Describe voices, music, and sound effects to create an audio scene."); },
  get text_to_speech() { return translateDisplayText("Choose a voice and turn your script into a new spoken take."); },
  get dialogue() { return translateDisplayText("Write a conversation with a voice for each line."); },
  get voice_design() { return translateDisplayText("Describe a voice and choose the words for its preview."); },
  get voice_clone() { return translateDisplayText("Start with a consented recording and a saved voice profile."); },
  get voice_clone_professional() { return translateDisplayText("Prepare a voice from verified recordings and training."); },
  get voice_manage() { return translateDisplayText("Keep your voices, versions and permissions together."); },
  get voice_revoke() { return translateDisplayText("Manage which voices can be used in future creations."); },
  get voice_change() { return translateDisplayText("Give an existing performance a different, authorized voice."); },
  get transcribe() { return translateDisplayText("Turn a recording into editable words and speaker timing."); },
  get forced_align() { return translateDisplayText("Match an existing transcript to the words in your recording."); },
  get pronunciation() { return translateDisplayText("Keep names, pauses and important words consistent."); },
  get music_generate() { return translateDisplayText("Start with a mood or your own lyrics, then shape the sound."); },
  get music_lyrics() { return translateDisplayText("Find the words for your next song."); },
  get music_extend() { return translateDisplayText("Continue a selected song from the moment you choose."); },
  get music_replace() { return translateDisplayText("Rework one section while keeping its source take."); },
  get music_cover() { return translateDisplayText("Explore a new arrangement of music you have permission to use."); },
  get music_stems() { return translateDisplayText("Separate a song into tracks you can work with."); },
  get music_variants() { return translateDisplayText("Explore another direction for an existing song."); },
  get music_add_vocals() { return translateDisplayText("Add a vocal part to an instrumental."); },
  get music_add_instrumental() { return translateDisplayText("Build accompaniment around your recording."); },
  get music_midi() { return translateDisplayText("Take musical notes and timing into your next session."); },
  get sound_generate() { return translateDisplayText("Describe an effect, a place or a layer of atmosphere."); },
  get sound_reference() { return translateDisplayText("Shape a sound around your visual or audio references."); },
  get sound_video_sync() { return translateDisplayText("Place sound against moments in your video."); },
  get import() { return translateDisplayText("Bring an existing audio asset into your document."); },
  get record() { return translateDisplayText("Record a performance and keep it as a source asset."); },
  get trim() { return translateDisplayText("Keep exactly the part of a recording you need."); },
  get split() { return translateDisplayText("Turn one recording into a sequence of clips."); },
  get join() { return translateDisplayText("Arrange your clips into one continuous track."); },
  get fade() { return translateDisplayText("Ease a recording in and out."); },
  get volume() { return translateDisplayText("Adjust the level of a selected recording."); },
  get mix() { return translateDisplayText("Place tracks together and balance their levels."); },
  get normalize() { return translateDisplayText("Set a consistent loudness for delivery."); },
  get speed() { return translateDisplayText("Change the pace of a recording."); },
  get voice_isolate() { return translateDisplayText("Bring the voice forward from a noisy recording."); },
  get noise_cleanup() { return translateDisplayText("Clean up distracting background sound."); },
  get source_separate() { return translateDisplayText("Work with the separate layers of a recording."); },
  get dubbing() { return translateDisplayText("Prepare speech and timing in another language."); },
  get subtitle_export() { return translateDisplayText("Turn reviewed, timed text into a subtitle file."); },
};

export function audioOperationsForGroup(
  items: MediaToolOperation[],
  group: AudioToolGroup,
) {
  const order = audioToolGroups.find((item) => item.value === group)!
    .operations as readonly string[];
  return items
    .filter(
      (item) => item.category === "audio" && order.includes(item.operation),
    )
    .sort((a, b) => order.indexOf(a.operation) - order.indexOf(b.operation));
}

export function audioSettingOptions(setting: MediaToolSetting) {
  const values =
    setting.enum_values ?? setting.numeric_enum ?? setting.enum ?? [];
  return values
    .filter((value): value is string | number | boolean =>
      ["string", "number", "boolean"].includes(typeof value),
    )
    .map((value, index) => ({
      value: JSON.stringify(value),
      label: /^(voice|voice_2|voice_id|voice_ref)$/.test(setting.key)
        ? setting.option_labels?.[String(value)] ?? String(value)
        : setting.option_labels?.[String(value)]
          ? translateDisplayText(setting.option_labels[String(value)])
          : typeof value === "boolean"
            ? translateDisplayText(value ? "On" : "Off")
            : typeof value === "number"
              ? value.toLocaleString(currentLocale())
              : /^(choice|variant)-/.test(value)
                ? localeText("interface.option_value0_d6a66b40", { defaultValue: "Option {{value0}}", value0: (index + 1).toLocaleString(currentLocale()) })
                : translateDisplayText(value.replaceAll("_", " ")),
    }));
}

export function audioVariantLabel(variant: MediaToolVariant) {
  if (variant.label?.trim()) return variant.label;
  const keys = new Set(variant.settings.map((s) => s.key));
  const name = keys.has("sound_prompt")
    ? localeText("interface.music_with_duration_control_719661e4", { defaultValue: "Music with duration control" })
    : keys.has("custom")
      ? localeText("interface.songs_with_custom_lyrics_185306c6", { defaultValue: "Songs with custom lyrics" })
      : keys.has("is_instrumental")
        ? localeText("interface.vocal_or_instrumental_music_4ffa9136", { defaultValue: "Vocal or instrumental music" })
        : keys.has("stability")
          ? localeText("interface.voice_with_stability_control_27c5f7e5", { defaultValue: "Voice with stability control" })
          : keys.has("emotion")
            ? localeText("interface.voice_with_emotion_control_af1d87c9", { defaultValue: "Voice with emotion control" })
            : keys.has("preview_model")
              ? localeText("interface.voice_with_preview_quality_6e02f8bd", { defaultValue: "Voice with preview quality" })
              : keys.has("diarize")
                ? localeText("interface.transcript_with_speaker_labels_f9752b1b", { defaultValue: "Transcript with speaker labels" })
                : keys.has("enable_itn")
                  ? localeText("interface.transcript_with_text_cleanup_2f9d8de4", { defaultValue: "Transcript with text cleanup" })
                  : keys.has("response_format")
                    ? localeText("interface.flexible_output_formats_9d3175d7", { defaultValue: "Flexible output formats" })
                    : localeText("interface.standard_controls_b545499d", { defaultValue: "Standard controls" });
  return name;
}

/** Customer guidance stays separate from supplier contract explanations. */
export function audioToolGuidance(operation: string, variant: MediaToolVariant): string[] {
  const keys = new Set(variant.settings.map(setting => setting.key));
  const guidance = (): string[] => {
  switch (operation) {
    case "music_generate":
      if (keys.has("sound_prompt")) return ["Describe the sound or write lyrics. Set the tempo in beats per minute."];
      if (keys.has("custom")) return ["Use a song idea for inspiration, or turn on custom lyrics mode to choose the words and musical style."];
      if (keys.has("is_instrumental")) return ["Instrumental music needs a description. For vocals, write your own lyrics or let the model write them from your description."];
      return [];
    case "music_extend":
      return ["Choose a starting point inside the source recording and how long to continue it."];
    case "music_replace":
    case "trim":
      return ["Choose a start and end inside the recording. The end must come after the start."];
    case "voice_design":
      return keys.has("auto_generate_text")
        ? ["Describe the voice in at least 20 characters. Use automatic preview text or write a script of 100–1,000 characters.", "Preview the voice before saving it to your profiles."]
        : ["Preview the voice before saving it to your profiles."];
    case "dialogue":
      return ["Choose a saved voice for every line. Use up to 10 different voices and 2,000 characters across the conversation."];
    case "transcribe":
      return keys.has("diarize")
        ? ["Separate audio channels supports recordings up to one hour with 1–5 channels.", "Names and key terms must be shorter than 50 characters and no more than five words each. Avoid brackets and backslashes."]
        : [];
    case "split":
      return ["Add cuts in time order, inside the recording. Each cut creates one more clip."];
    case "fade":
      return ["Keep the fade-in and fade-out from overlapping."];
    case "mix":
      return ["Choose a source clip and a section for each track, then set where it starts in the mix. The mix ends with the last track."];
    case "subtitle_export":
      return ["Add captions in time order, with each end after its start and inside the recording. Captions can overlap.", "Exports contain text and timed subtitles. Video export is not available here."];
    default:
      return [];
  }
  };
  return guidance().map(translateDisplayText);
}

export function audioVariantDefaults(
  variant: MediaToolVariant,
): Record<string, unknown> {
  return Object.fromEntries(
    variant.settings
      .filter((field) => field.default !== undefined)
      .map((field) => [field.key, structuredClone(field.default)]),
  );
}

export function audioTextInput(
  variant: MediaToolVariant,
  kind: "Prompt" | "Script",
) {
  const input = kind === "Prompt" ? variant.prompt : variant.script;
  return input
    ? { required: input.required, maxLength: input.max_length }
    : null;
}

export function audioSourceIssue(
  asset: V2Asset,
  reference: MediaToolReference,
): string | null {
  if (asset.status !== "available") return translateDisplayText("This asset is not available.");
  if (
    reference.media_kind !== "mixed" &&
    reference.media_kind !== asset.media_type
  )
    return translateDisplayText("Choose a matching audio source.");
  if (
    reference.media_kind === "mixed" &&
    !["audio", "video"].includes(asset.media_type)
  )
    return translateDisplayText("Choose audio or video.");
  if (
    reference.accepted_formats?.length &&
    !reference.accepted_formats.includes(asset.mime_type)
  )
    return translateDisplayText("This format is not supported by the selected model.");
  if (reference.max_bytes && asset.size_bytes > reference.max_bytes)
    return localeText("interface.choose_a_file_smaller_than_value0_c2fabbd4", { defaultValue: "Choose a file smaller than {{value0}}.", value0: formatAudioBytes(reference.max_bytes) });
  if (
    reference.max_duration_seconds &&
    asset.duration_ms &&
    asset.duration_ms > reference.max_duration_seconds * 1000
  )
    return localeText("interface.choose_a_recording_up_to_value0_seconds_6fd0a946", { defaultValue: "Choose a recording up to {{value0}} seconds.", value0: reference.max_duration_seconds.toLocaleString(currentLocale()) });
  return null;
}

export function formatAudioBytes(bytes: number) {
  return `${new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 1 }).format(bytes / 1_000_000)} MB`;
}

export function audioSettingHint(setting: MediaToolSetting) {
  const range =
    setting.minimum !== undefined || setting.maximum !== undefined
      ? `${setting.minimum?.toLocaleString(currentLocale()) ?? localeText("interface.no_minimum_78626b40", { defaultValue: "No minimum" })}–${setting.maximum?.toLocaleString(currentLocale()) ?? localeText("interface.no_maximum_b82653f5", { defaultValue: "no maximum" })}`
      : "";
  return [
    range,
    setting.max_length
      ? localeText("interface.up_to_value0_characters_9d883c89", { defaultValue: "Up to {{value0}} characters", value0: setting.max_length.toLocaleString(currentLocale()) })
      : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

export function audioSettingConflict(
  key: string,
  values: Record<string, unknown>,
): string | null {
  if (
    [
      "title",
      "style",
      "negative_tags",
      "auto_lyrics",
      "style_weight",
      "weirdness_constraint",
      "audio_weight",
    ].includes(key) &&
    values.custom === false
  )
    return translateDisplayText("Choose custom lyrics mode to use this control.");
  if (key === "bitrate" && values.format && values.format !== "mp3")
    return translateDisplayText("Bitrate applies to MP3 output.");
  if (
    ["lyrics", "lyrics_optimizer"].includes(key) &&
    values.is_instrumental === true
  )
    return translateDisplayText("Instrumental music uses a description without lyrics.");
  if (key === "lyrics" && values.lyrics_optimizer === true)
    return translateDisplayText("Turn off automatic lyrics to write your own.");
  if (
    key === "diarization_threshold" &&
    (values.diarize !== true || values.num_speakers !== undefined)
  )
    return translateDisplayText("Enable speaker labels and leave speaker count automatic.");
  if (key === "multichannel_output_style" && values.use_multi_channel !== true)
    return translateDisplayText("Enable separate audio channels first.");
  if (
    key === "detect_speaker_roles" &&
    (values.diarize !== true || values.use_multi_channel === true)
  )
    return translateDisplayText("Use speaker labels without separate audio channels.");
  if (
    ["entity_detection", "entity_redaction"].includes(key) &&
    values.multichannel_output_style === "combined"
  )
    return translateDisplayText("Entity controls require separate channel output.");
  return null;
}
